/**
 * HelpTooltip Component - Theme-aware help content display
 *
 * A reusable tooltip component for displaying help content with full theme integration.
 * Supports rich HTML content, async loading, and automatic positioning.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Tooltip,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import HelpContentProvider from '../../services/HelpContentProvider';

// ============================================================================
// TYPES
// ============================================================================

export interface HelpTooltipProps {
  /**
   * Help content ID to load
   */
  helpId: string;

  /**
   * Optional static help content (overrides helpId)
   */
  content?: string;

  /**
   * Tooltip placement
   * @default 'top'
   */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

  /**
   * Icon size
   * @default 'small'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Custom tooltip title (defaults to "Get help")
   */
  tooltipTitle?: string;

  /**
   * Max width for tooltip content
   * @default 300
   */
  maxWidth?: number;

  /**
   * Callback when help is clicked
   */
  onClick?: (helpId: string) => void;

  /**
   * Custom styles for the icon button
   */
  sx?: SxProps<Theme>;

  /**
   * Test ID for testing
   */
  'data-testid'?: string;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Whether to show loading indicator
   * @default true
   */
  showLoading?: boolean;

  /**
   * HTML element type for the icon button.
   * Use 'span' when HelpTooltip is rendered inside another button (e.g. AccordionSummary)
   * to avoid `<button> inside <button>` DOM nesting violation.
   * @default 'button'
   */
  component?: React.ElementType;

  /**
   * Fully suppress the hover tooltip (renders only the icon button).
   * Used when a parent opens its own hover popover and the lightweight tooltip
   * would double-render / fight it.
   * @default false
   */
  disableTooltip?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * HelpTooltip - Theme-aware help content display
 *
 * Usage:
 * ```tsx
 * <HelpTooltip helpId="user-name-field" />
 *
 * <HelpTooltip
 *   content="Enter your full name as it appears on official documents"
 *   placement="right"
 * />
 *
 * <HelpTooltip
 *   helpId="advanced-settings"
 *   onClick={(helpId) => console.log('Help clicked:', helpId)}
 * />
 * ```
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  helpId,
  content,
  placement = 'top',
  size = 'small',
  tooltipTitle = 'Get help',
  maxWidth = 300,
  onClick,
  sx,
  'data-testid': dataTestId,
  disabled = false,
  showLoading = true,
  component,
  disableTooltip = false,
}) => {
  const theme = useTheme();
  const [helpContent, setHelpContent] = useState<string | null>(content || null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load help content if helpId is provided and content is not static
  useEffect(() => {
    if (!helpId || content) return;

    const loadContent = async () => {
      setLoading(true);
      setError(null);

      try {
        const provider = HelpContentProvider.getInstance();
        const loadedContent = await provider.getContentAsync(helpId);

        if (loadedContent) {
          setHelpContent(loadedContent);
        } else {
          setError(`Help content not found: ${helpId}`);
        }
      } catch (err) {
        setError(`Failed to load help content: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [helpId, content]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    onClick?.(helpId);
  }, [disabled, onClick, helpId]);

  // Determine what to show in tooltip
  const getTooltipContent = () => {
    if (loading && showLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            p: theme.spacing(1),
          }}
        >
          <CircularProgress
            size={16}
            sx={{ color: theme.palette.primary.contrastText }}
          />
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.contrastText }}
          >
            Loading help...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.error.contrastText,
            fontStyle: 'italic'
          }}
        >
          {error}
        </Typography>
      );
    }

    if (helpContent) {
      return (
        <Box sx={{ maxWidth }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.primary.contrastText,
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap'
            }}
            dangerouslySetInnerHTML={{ __html: helpContent }}
          />
        </Box>
      );
    }

    return tooltipTitle;
  };

  // Calculate icon size based on size prop
  const getIconSize = () => {
    switch (size) {
      case 'large': return 'medium';
      case 'medium': return 'small';
      case 'small':
      default: return 'inherit';
    }
  };

  const iconButton = (
    <span>
      <IconButton
        {...(component ? { component } : {})}
        size={size}
        disabled={disabled}
        onClick={handleClick}
        data-testid={dataTestId || 'help-tooltip'}
        aria-label={tooltipTitle}
        sx={{
          color: theme.palette.action.active,
          transition: theme.transitions.create(['color', 'background-color'], {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          },
          '&:disabled': {
            color: theme.palette.action.disabled,
          },
          ...sx,
        }}
      >
        <HelpOutlineIcon fontSize={getIconSize()} />
      </IconButton>
    </span>
  );

  if (disableTooltip) {
    return iconButton;
  }

  return (
    <Tooltip
      title={getTooltipContent()}
      placement={placement}
      arrow
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            border: `1px solid ${theme.palette.primary.dark}`,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[4],
            maxWidth: maxWidth + 20, // Add padding for arrow
            fontSize: theme.typography.body2.fontSize,
          },
          '& .MuiTooltip-arrow': {
            color: theme.palette.primary.main,
            '&::before': {
              border: `1px solid ${theme.palette.primary.dark}`,
            },
          },
        },
      }}
    >
      {iconButton}
    </Tooltip>
  );
};

HelpTooltip.displayName = 'HelpTooltip';

export default HelpTooltip;